import React, { useState, useEffect } from "react";
import Nav from "../../components/Home/Nav";
import Footer from "../../components/Home/Footer";
import axios from "axios";
import {
  HiPhotograph,
  HiVideoCamera,
  HiFilter,
  HiX,
  HiPlay,
  HiSparkles,
  HiExternalLink,
  HiViewGrid,
  HiChevronLeft,
  HiChevronRight,
  HiChevronUp,
  HiChevronDown,
} from "react-icons/hi";

// Utility helper to render video iframe/player for various platforms
const VideoEmbed = ({ url, formatType, title }) => {
  if (!url) return null;

  // 1. YouTube Standard & Shorts
  const ytMatch = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([\w-]{11})/
  );
  if (ytMatch && ytMatch[1]) {
    const videoId = ytMatch[1];
    const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    return (
      <iframe
        src={embedUrl}
        title={title || "YouTube Video"}
        className="w-full h-full border-0 rounded-2xl"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      ></iframe>
    );
  }

  // 2. Direct Video File (.mp4, .webm, etc.)
  if (url.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i)) {
    return (
      <video
        src={url}
        controls
        autoPlay
        className="w-full h-full object-contain rounded-2xl bg-black"
      >
        Your browser does not support the video tag.
      </video>
    );
  }

  // 3. Fallback Embed (TikTok, Facebook, Instagram Reels, external link embed)
  return (
    <iframe
      src={url}
      title={title || "Video Player"}
      className="w-full h-full border-0 rounded-2xl bg-slate-900"
      allowFullScreen
    ></iframe>
  );
};

function Gallery() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all"); // 'all', 'photo', 'video_16_9', 'video_9_16'
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [activeModalIndex, setActiveModalIndex] = useState(null);

  useEffect(() => {
    fetchPublicGallery();
  }, []);

  const fetchPublicGallery = async () => {
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const response = await axios.get(`${apiUrl}/api/gallery`);
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error loading public gallery:", error);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = ["All", ...Array.from(new Set(items.map((i) => i.category || "General")))];

  const filteredItems = items.filter((item) => {
    let matchesTab = true;
    if (activeTab === "photo") {
      matchesTab = item.media_type === "image";
    } else if (activeTab === "video_16_9") {
      matchesTab = item.media_type === "video" && item.format_type === "16:9";
    } else if (activeTab === "video_9_16") {
      matchesTab = item.media_type === "video" && item.format_type === "9:16";
    }

    const matchesCategory =
      selectedCategory === "All" || item.category === selectedCategory;

    return matchesTab && matchesCategory;
  });

  const activeModalItem =
    activeModalIndex !== null && filteredItems[activeModalIndex]
      ? filteredItems[activeModalIndex]
      : null;

  const isReelMode =
    activeModalItem &&
    (activeModalItem.format_type === "9:16" ||
      (activeModalItem.url &&
        (activeModalItem.url.includes("/shorts/") ||
          activeModalItem.url.includes("tiktok.com") ||
          activeModalItem.url.includes("/reel/"))));

  // When viewing a 9:16 Reel, filter active items list to ONLY Reels!
  const reelItemsList = isReelMode
    ? filteredItems.filter(
        (item) =>
          item.format_type === "9:16" ||
          (item.url &&
            (item.url.includes("/shorts/") ||
              item.url.includes("tiktok.com") ||
              item.url.includes("/reel/")))
      )
    : filteredItems;

  const currentReelSubIndex = isReelMode
    ? reelItemsList.findIndex((item) => item.id === activeModalItem.id)
    : -1;

  const handlePrevItem = () => {
    if (isReelMode && reelItemsList.length > 0) {
      const newSubIdx =
        currentReelSubIndex <= 0 ? reelItemsList.length - 1 : currentReelSubIndex - 1;
      const targetItem = reelItemsList[newSubIdx];
      const mainIdx = filteredItems.findIndex((i) => i.id === targetItem.id);
      if (mainIdx !== -1) setActiveModalIndex(mainIdx);
    } else if (filteredItems.length > 0) {
      setActiveModalIndex((prevIndex) =>
        prevIndex === 0 ? filteredItems.length - 1 : prevIndex - 1
      );
    }
  };

  const handleNextItem = () => {
    if (isReelMode && reelItemsList.length > 0) {
      const newSubIdx =
        currentReelSubIndex >= reelItemsList.length - 1 ? 0 : currentReelSubIndex + 1;
      const targetItem = reelItemsList[newSubIdx];
      const mainIdx = filteredItems.findIndex((i) => i.id === targetItem.id);
      if (mainIdx !== -1) setActiveModalIndex(mainIdx);
    } else if (filteredItems.length > 0) {
      setActiveModalIndex((prevIndex) =>
        prevIndex === filteredItems.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const [touchStartY, setTouchStartY] = useState(null);
  const [touchEndY, setTouchEndY] = useState(null);

  const handleTouchStart = (e) => {
    setTouchStartY(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e) => {
    setTouchEndY(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!touchStartY || !touchEndY) return;
    const distance = touchStartY - touchEndY;
    const isSwipeUp = distance > 50;
    const isSwipeDown = distance < -50;

    if (isSwipeUp) {
      handleNextItem();
    } else if (isSwipeDown) {
      handlePrevItem();
    }
    setTouchStartY(null);
    setTouchEndY(null);
  };

  const handleWheelScroll = (e) => {
    if (!isReelMode) return;
    if (e.deltaY > 50) {
      handleNextItem();
    } else if (e.deltaY < -50) {
      handlePrevItem();
    }
  };

  // Keyboard navigation & vertical wheel swipe for TikTok / Shorts vertical feed
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeModalIndex === null) return;
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        handlePrevItem();
      } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        handleNextItem();
      } else if (e.key === "Escape") {
        setActiveModalIndex(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeModalIndex, filteredItems, currentReelSubIndex, isReelMode]);

  return (
    <div className="bg-slate-50 min-h-screen flex flex-col pt-20 md:pt-28">
      <Nav />

      {/* Hero Header */}
      <section className="bg-slate-900 text-white py-16 md:py-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="max-w-7xl mx-auto text-center space-y-4 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 text-blue-400 rounded-full text-[10px] font-black uppercase tracking-widest border border-blue-500/20">
            <HiSparkles size={16} /> Official Media Gallery
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-black tracking-tight leading-tight">
            Our Works & Field Project <span className="text-blue-500">Showcase</span>
          </h1>
          <p className="text-sm md:text-lg text-slate-300 max-w-3xl mx-auto font-medium leading-relaxed">
            Explore high-resolution site photos, project videos, and vertical reels demonstrating S.D. Enterprises' proven waterproofing mastery across Nepal.
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-6 py-12 flex-1 w-full space-y-8">
        {/* Navigation Filter Tabs */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white p-4 rounded-3xl border border-slate-200/80 shadow-sm">
          {/* Main Type Tabs */}
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            <button
              onClick={() => setActiveTab("all")}
              className={`px-5 py-2.5 rounded-2xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeTab === "all"
                  ? "bg-slate-900 text-white shadow-lg"
                  : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
            >
              <HiViewGrid size={16} /> All Media ({items.length})
            </button>
            <button
              onClick={() => setActiveTab("photo")}
              className={`px-5 py-2.5 rounded-2xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeTab === "photo"
                  ? "bg-blue-600 text-white shadow-lg"
                  : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
            >
              <HiPhotograph size={16} /> Photos
            </button>
            <button
              onClick={() => setActiveTab("video_16_9")}
              className={`px-5 py-2.5 rounded-2xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeTab === "video_16_9"
                  ? "bg-purple-600 text-white shadow-lg"
                  : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
            >
              <HiVideoCamera size={16} /> Videos (16:9)
            </button>
            <button
              onClick={() => setActiveTab("video_9_16")}
              className={`px-5 py-2.5 rounded-2xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeTab === "video_9_16"
                  ? "bg-pink-600 text-white shadow-lg"
                  : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
            >
              <HiVideoCamera size={16} /> Shorts & Reels (9:16)
            </button>
          </div>

          {/* Category Dropdown Filter */}
          <div className="flex items-center gap-3 w-full md:w-auto">
            <HiFilter className="text-slate-400" size={18} />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full md:w-auto px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold text-slate-800 focus:outline-none focus:border-blue-500"
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat === "All" ? "All Project Categories" : cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Gallery Grid Display */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-12 h-12 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-4">
              Loading Gallery Items...
            </p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200/80 p-16 text-center shadow-sm">
            <HiPhotograph className="mx-auto text-slate-300 mb-4" size={56} />
            <h3 className="text-lg font-bold text-slate-800">No Gallery Media Available</h3>
            <p className="text-xs text-slate-500 mt-1">
              There are currently no items matching your selected category or filter tab.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-start">
            {filteredItems.map((item, idx) => (
              <div
                key={item.id}
                onClick={() => setActiveModalIndex(idx)}
                className="bg-white rounded-3xl border border-slate-200/80 shadow-sm overflow-hidden group cursor-pointer hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col relative"
              >
                {/* Media Thumbnail Box */}
                <div
                  className={`relative bg-slate-950 overflow-hidden ${
                    item.format_type === "9:16" ? "h-96" : "h-56"
                  }`}
                >
                  {item.media_type === "image" ? (
                    <div className="w-full h-full relative">
                      <img
                        src={item.url}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      {/* Card Next / Prev Quick Switcher Controls */}
                      {filteredItems.filter((i) => i.media_type === "image").length > 1 && (
                        <div className="absolute inset-0 flex items-center justify-between p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrevItem();
                            }}
                            className="p-2 bg-slate-900/80 text-white rounded-full hover:bg-blue-600 shadow-md transition-all hover:scale-110"
                            title="Previous Image"
                          >
                            <HiChevronLeft size={18} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNextItem();
                            }}
                            className="p-2 bg-slate-900/80 text-white rounded-full hover:bg-blue-600 shadow-md transition-all hover:scale-110"
                            title="Next Image"
                          >
                            <HiChevronRight size={18} />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-full h-full relative flex items-center justify-center">
                      {item.thumbnail_url ? (
                        <img
                          src={item.thumbnail_url}
                          alt={item.title}
                          className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-slate-400">
                          <HiVideoCamera size={44} />
                          <span className="text-[10px] font-black uppercase tracking-widest">
                            Video Link
                          </span>
                        </div>
                      )}
                      {/* Play Icon Badge */}
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/20 transition-colors">
                        <div className="w-14 h-14 bg-blue-600/90 text-white rounded-full flex items-center justify-center shadow-2xl shadow-blue-500/50 group-hover:scale-110 transition-transform">
                          <HiPlay size={30} className="ml-1" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Top Badges */}
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider ${
                        item.media_type === "image"
                          ? "bg-blue-600 text-white shadow-md"
                          : item.format_type === "9:16"
                          ? "bg-pink-600 text-white shadow-md"
                          : "bg-purple-600 text-white shadow-md"
                      }`}
                    >
                      {item.media_type === "image"
                        ? "Photo"
                        : item.format_type === "9:16"
                        ? "Shorts / Reel"
                        : "Video"}
                    </span>
                    <span className="px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider bg-slate-900/80 text-white backdrop-blur-sm border border-white/20">
                      {item.format_type}
                    </span>
                  </div>
                </div>

                {/* Information Footer */}
                <div className="p-5 space-y-2 flex-1 flex flex-col justify-between">
                  <div>
                    <span className="text-[9px] font-black uppercase tracking-widest text-blue-600 bg-blue-50 px-2.5 py-1 rounded-md border border-blue-100">
                      {item.category || "General"}
                    </span>
                    <h3 className="text-base font-bold text-slate-900 mt-2 line-clamp-1 group-hover:text-blue-600 transition-colors">
                      {item.title}
                    </h3>
                    {item.description && (
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-relaxed font-medium">
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Lightbox / Video Player Modal with TikTok & YouTube Shorts Swiping Mode */}
      {activeModalItem && (
        <div
          onWheel={handleWheelScroll}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="fixed inset-0 z-[200] bg-slate-950/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-300 select-none"
        >
          <div className="relative flex items-center justify-center gap-3 sm:gap-6 max-w-full max-h-full">
            {/* Standard Previous Button (Horizontal Mode) */}
            {!isReelMode && filteredItems.length > 1 && (
              <button
                onClick={handlePrevItem}
                className="p-3.5 bg-slate-900 hover:bg-blue-600 text-white rounded-full border-2 border-slate-700 shadow-2xl transition-all hover:scale-110 active:scale-95 flex-shrink-0"
                title="Previous Media (Left Arrow)"
              >
                <HiChevronLeft size={28} />
              </button>
            )}

            {/* Main Modal Card Container */}
            <div
              className={`relative bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl flex flex-col ${
                isReelMode
                  ? "w-[360px] sm:w-[390px] h-[85vh] min-h-[550px] max-h-[740px]"
                  : "w-[92vw] max-w-5xl h-auto max-h-[92vh]"
              }`}
            >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-3.5 sm:p-4 border-b border-slate-800 bg-slate-900 text-white z-10">
              <div>
                <span
                  className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded border ${
                    isReelMode
                      ? "text-pink-400 bg-pink-500/10 border-pink-500/20"
                      : "text-blue-400 bg-blue-500/10 border-blue-500/20"
                  }`}
                >
                  {isReelMode
                    ? `Shorts & Reels Feed • ${currentReelSubIndex + 1} of ${reelItemsList.length}`
                    : `${activeModalItem.category || "General"} • ${activeModalItem.format_type} • ${activeModalIndex + 1} of ${filteredItems.length}`}
                </span>
                <h3 className="text-sm sm:text-base font-bold text-white mt-1 line-clamp-1">
                  {activeModalItem.title}
                </h3>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={activeModalItem.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-slate-400 hover:text-white rounded-xl bg-slate-800 transition-colors"
                  title="Open Original Video Link"
                >
                  <HiExternalLink size={18} />
                </a>
                <button
                  onClick={() => setActiveModalIndex(null)}
                  className="p-2 text-slate-400 hover:text-white rounded-xl bg-slate-800 transition-colors"
                >
                  <HiX size={18} />
                </button>
              </div>
            </div>

            {/* Modal Media Body */}
            <div
              className={`flex-1 relative bg-black flex items-center justify-center overflow-hidden ${
                !isReelMode && activeModalItem.media_type === "video"
                  ? "w-full aspect-video min-h-[300px] sm:min-h-[420px] md:min-h-[500px]"
                  : "w-full h-full"
              }`}
            >
              {activeModalItem.media_type === "image" ? (
                <img
                  src={activeModalItem.url}
                  alt={activeModalItem.title}
                  className="w-full h-full object-contain max-h-[75vh]"
                />
              ) : (
                <div className="w-full h-full relative flex items-center justify-center">
                  <VideoEmbed
                    url={activeModalItem.url}
                    formatType={activeModalItem.format_type}
                    title={activeModalItem.title}
                  />
                  {isReelMode && (
                    <div className="absolute bottom-3 right-3 bg-slate-900/80 backdrop-blur-md text-[10px] font-bold text-white px-3 py-1.5 rounded-full border border-white/20 shadow-lg pointer-events-none flex items-center gap-1.5 animate-pulse">
                      <span>Swipe / Scroll Up & Down</span> ↕
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Description & Photo Thumbnail Strip */}
            <div className="bg-slate-900 border-t border-slate-800 p-3.5 space-y-3">
              {activeModalItem.description && (
                <p className="text-xs text-slate-300 font-medium line-clamp-2">
                  {activeModalItem.description}
                </p>
              )}

              {/* Bottom Thumbnail Strip for Photos */}
              {activeModalItem.media_type === "image" && filteredItems.filter((i) => i.media_type === "image").length > 1 && (
                <div className="flex items-center gap-2 overflow-x-auto pb-1 custom-scrollbar">
                  {filteredItems
                    .filter((i) => i.media_type === "image")
                    .map((photoItem) => {
                      const photoIdx = filteredItems.findIndex((i) => i.id === photoItem.id);
                      const isSelected = photoIdx === activeModalIndex;
                      return (
                        <button
                          key={photoItem.id}
                          type="button"
                          onClick={() => setActiveModalIndex(photoIdx)}
                          className={`relative w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all ${
                            isSelected
                              ? "border-blue-500 scale-105 shadow-md shadow-blue-500/30"
                              : "border-slate-800 opacity-60 hover:opacity-100"
                          }`}
                        >
                          <img
                            src={photoItem.url}
                            alt={photoItem.title}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
          </div>

          {/* Standard Next Button (Horizontal Mode) */}
          {!isReelMode && filteredItems.length > 1 && (
            <button
              onClick={handleNextItem}
              className="p-3.5 bg-slate-900 hover:bg-blue-600 text-white rounded-full border-2 border-slate-700 shadow-2xl transition-all hover:scale-110 active:scale-95 flex-shrink-0"
              title="Next Media (Right Arrow)"
            >
              <HiChevronRight size={28} />
            </button>
          )}

          {/* TikTok / Shorts Vertical Reel Swiping Navigation Bar (Reel Mode) */}
          {isReelMode && reelItemsList.length > 1 && (
            <div className="flex flex-col gap-3 flex-shrink-0">
              <button
                onClick={handlePrevItem}
                className="p-3.5 bg-slate-950 hover:bg-pink-600 text-white rounded-full border-2 border-slate-700 shadow-2xl transition-all hover:scale-110 active:scale-95"
                title="Previous Reel (Up Arrow / Swipe Up)"
              >
                <HiChevronUp size={28} className="text-white" />
              </button>
              <button
                onClick={handleNextItem}
                className="p-3.5 bg-slate-950 hover:bg-pink-600 text-white rounded-full border-2 border-slate-700 shadow-2xl transition-all hover:scale-110 active:scale-95"
                title="Next Reel (Down Arrow / Swipe Down)"
              >
                <HiChevronDown size={28} className="text-white" />
              </button>
            </div>
          )}
        </div>
      </div>
    )}

      <Footer />
    </div>
  );
}

export default Gallery;
